import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaTimes } from 'react-icons/fa';
import { toIdentifierMask } from '../../services/mask';
import { PayrollViewModel } from '../../models/payroll/PayrollViewModel';
import CustomButton from '../../components/CustomButton/CustomButton';
import api from '../../services/api';
import { useAuth } from '../../contexts/auth';

interface PayrollsListProps {
  payrolls: PayrollViewModel[];
  openPayrollInNewTab?: boolean;
  refreshPayrolls: () => void;
}

export default function PayrollsList({
  payrolls,
  refreshPayrolls,
  openPayrollInNewTab = false,
}: PayrollsListProps) {
  const {
    user: { organization },
  } = useAuth();
  const [payrollsSorted, setPayrollsSorted] = useState<PayrollViewModel[]>([]);

  function formatCell(
    top: JSX.Element | string,
    bottom: JSX.Element | string = ''
  ) {
    return (
      <div className='d-flex flex-column'>
        <div>{top}</div>
        {bottom ? (
          <div>
            <small className='text-muted'>{bottom}</small>
          </div>
        ) : undefined}
      </div>
    );
  }

  const generatePayroll = async () => {
    try {
      await api.post(`organizations/${organization.id}/payrolls`);
      toast.success('Folha gerada com sucesso');
      refreshPayrolls();
    } catch (e) {
      toast.error('Ocorreu um problema ao gerar a folha');
    }
  };

  const remove = useCallback(
    async (customer: PayrollViewModel) => {
      try {
        const displayName = `${customer.companyName}`;
        if (
          // eslint-disable-next-line no-restricted-globals
          confirm(
            `Você tem certeza que deseja remover a folha da Empresa "${displayName}"? Essa ação não poderá ser desfeita.`
          )
        ) {
          await api.delete(
            `organizations/${organization.id}/payrolls/${customer.id}`
          );

          refreshPayrolls();
          toast.success(`A folha da Empresa "${displayName}" foi removida`);
        }
      } catch (e) {
        toast.error(
          'Houve um problema ao tentar remover a folha. Tente novamente.'
        );
      }
    },
    [organization.id, refreshPayrolls, toast]
  );

  useEffect(() => {
    if (payrolls.length) {
      setPayrollsSorted(
        payrolls.sort((a, b) =>
          new Date(a.startDate) < new Date(b.startDate) ? 1 : -1
        )
      );
    }
  }, [payrolls]);

  return (
    <>
      <table className='table table-hover'>
        <thead>
          <tr>
            <th scope='col'>Empresa</th>
            <th scope='col'>Início</th>
            <th scope='col'>Fim</th>
            <th scope='col'>Gerada em</th>
            <th scope='col'>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {payrollsSorted.map((p: PayrollViewModel) => {
            const startDate = new Date(p.startDate);
            const endDate = new Date(p.endDate);
            const createdAt = new Date(p.createdAt);
            const editLink = (
              <Link
                target={openPayrollInNewTab ? '_blank' : '_self'}
                to={`/payrolls/${p.id}`}
              >
                {p.companyName}
              </Link>
            );
            return (
              <tr key={p.id}>
                <td>
                  {formatCell(
                    editLink,
                    toIdentifierMask(p.companyIdentifier, p.companyType)
                  )}
                </td>
                <td>{formatCell(startDate.toLocaleDateString())}</td>
                <td>{formatCell(endDate.toLocaleDateString())}</td>
                <td>
                  {formatCell(
                    createdAt.toLocaleDateString(),
                    createdAt.toLocaleTimeString()
                  )}
                </td>
                <td className='text-end'>
                  {p.canRemove && (
                    <CustomButton
                      className='btn btn-sm btn-danger'
                      type='button'
                      handleClick={() => remove(p)}
                    >
                      <FaTimes />
                    </CustomButton>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className='mb-3 d-flex justify-content-end'>
        <CustomButton
          type='button'
          handleClick={generatePayroll}
          className='btn btn-secondary btn-sm'
        >
          Verificar pendências
        </CustomButton>
      </div>
    </>
  );
}
